import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
     Chart as ChartJS,
     ArcElement,
     CategoryScale,
     LinearScale,
     PointElement,
     LineElement,
     Title,
     Tooltip,
     Legend,
} from "chart.js";
import RGL, { Responsive, WidthProvider } from "react-grid-layout";
import DoughnutGraph from "./views/DoughnutGraph";
import LineGraph from "./views/LineGraph";
import CarouselGraph from "./views/CarouselGraph";
import ColumnGraph from "./views/ColumnGraph";
import CommunicationsGraph from "./views/CommunicationsGraph";
import { dashboardConstants } from "../../../_redux/_constants/dashboard.constants";
import {
     ContentRightWr,
     MainLeftTitle,
     CreateButton,
} from "../../_uiComponents/WrapperComponents";
import CampaignDonutsGraph from "./views/CampaignDonutsGraph";
import { useDispatch } from "react-redux";
import {
     dashBoardGetCampaignPageViewSummaryRequest,
     dashBoardGetCommsPageViewSummaryRequest,
     dashBoardGetParticipantActivationSummaryRequest,
     dashBoardGetParticipantLoginSummaryRequest,
     dashBoardGetRewardIssuedByCampaignRequest,
     dashBoardGetDashboardSummaryRequest,
     getEmailTrackingSummaryRequest,
     getEmailDispatchSummaryRequest,
     getQ4OilMasterSummaryRequest,
     getWearTearSummaryRequest,
     getAbuDhabiSummaryRequest,
     getDevicedUsedSummaryRequest,
     getAreaListRequest,
     dashBoardGetAreaManagersLeaguesRankSummaryRequest,
     getMostActiveDetailChartDataRequest,
     getTLCAreaListRequest,
     dashBoardGetTLCParticipantActivationSummaryRequest,
} from "../../../_redux/_actions";
import { participantJobRoleIds } from "../../../_redux/_constants";
import UiSelect from "../../_uiComponents/common/form/UiSelectDefault";

import StatisticCarousel from "./views/StatisticCarousel";
import TlcParticipants from "./views/TlcParticipants";
import RewardIssuedByCampaign from "./views/RewardIssuedByCampaign";
import UserLoginSummary from "./views/UserLoginSummary";
import EmailTracking from "./views/EmailTracking";
import CampaignPageViewSummary from "./views/CampaignPageViewSummary";
import CommsPageSummary from "./views/CommsPageSummary";
import EmailDespatches from "./views/EmailDespatches";
import DeviceUsed from "./views/DeviceUsed";
import RewardsEarnedByArea from "./views/RewardsEarnedByArea";
import RewardSpendAganistBudget from "./views/RewardSpendAganistBudget";
import MostActive from "./views/MostActive";
import WinningParticipants from "./views/WinningParticipants";
import PerformanceQ4CastrolMasters from "./views/PerformanceQ4CastrolMasters";
import EquipeParticipants from "./views/EquipeParticipants";
import ChatWindow from "./views/ChatWindow";

const ResponsiveGridLayout = WidthProvider(Responsive);

function DashboardWrapper() {
     const actionDispatch = useDispatch();

     const [userType, setUserType] = useState("0");

     const getApplicationTypeInitialData = (value) => {
          actionDispatch(
               dashBoardGetDashboardSummaryRequest({ applicationId: value }),
          );

          setUserType(value);
     };

     const jobRolesOptions = {
          0: [
               {
                    value: "0",
                    label: "All",
               },

               {
                    value: participantJobRoleIds.PARTICIPANT_JOB_ROLE_ID_SERVICE_MANAGER,
                    label: "Participant Service Manager",
               },
               {
                    value: participantJobRoleIds.PARTICIPANT_JOB_ROLE_ID_SERVICE_ADVISOR,
                    label: "Participant Service Advisor",
               },
               {
                    value: participantJobRoleIds.PARTICIPANT_JOB_ROLE_ID_SERVICE_AREA_MANAGER,
                    label: "Participant Service Area Manager",
               },
               {
                    value: participantJobRoleIds.TLC_PARTICIPANT_CUSTOMER,
                    label: "TLC Participant Customer",
               },
               {
                    value: participantJobRoleIds.TLC_PARTICIPANT_AREA_MANAGER,
                    label: "TLC Participant Area Manager",
               },
          ],

          1: [
               {
                    value: "0",
                    label: "All",
               },

               {
                    value: participantJobRoleIds.PARTICIPANT_JOB_ROLE_ID_SERVICE_MANAGER,
                    label: "Participant Service Manager",
               },
               {
                    value: participantJobRoleIds.PARTICIPANT_JOB_ROLE_ID_SERVICE_ADVISOR,
                    label: "Participant Service Advisor",
               },
               {
                    value: participantJobRoleIds.PARTICIPANT_JOB_ROLE_ID_SERVICE_AREA_MANAGER,
                    label: "Participant Service Area Manager",
               },
          ],
          3: [
               {
                    value: "0",
                    label: "All",
               },

               {
                    value: participantJobRoleIds.TLC_PARTICIPANT_CUSTOMER,
                    label: "TLC Participant Customer",
               },
               {
                    value: participantJobRoleIds.TLC_PARTICIPANT_AREA_MANAGER,
                    label: "TLC Participant Area Manager",
               },
          ],
     };

     const getDashboardSummaryData = () => {
          actionDispatch(
               dashBoardGetDashboardSummaryRequest({ applicationId: userType }),
          );
     };

     const getQ4OilMasterSummary = (
          startDate = null,
          roleId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               getQ4OilMasterSummaryRequest({
                    roleId,
                    startDate,
                    applicationId,
               }),
          );
     };

     const getWearTearSummary = (
          startDate = null,
          roleId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               getWearTearSummaryRequest({
                    roleId,
                    startDate,
                    applicationId,
               }),
          );
     };

     const getAbuDhabiSummary = (
          startDate = null,
          endDate = null,
          roleId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               getAbuDhabiSummaryRequest({
                    roleId,
                    startDate,
                    endDate,
                    applicationId,
               }),
          );
     };

     const getParticipantActivationSummaryData = (
          startDate = null,
          endDate = null,
          areaId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               dashBoardGetParticipantActivationSummaryRequest({
                    startDate,
                    endDate,
                    areaId,
                    applicationId,
               }),
          );
     };

     const getTLCParticipantActivationSummaryData = (
          startDate = null,
          endDate = null,
          areaId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               dashBoardGetTLCParticipantActivationSummaryRequest({
                    startDate,
                    endDate,
                    areaId,
                    applicationId,
               }),
          );
     };



     const getRewardIssuedByCampaignData = (
          startDate = null,
          endDate = null,
          roleId = null,
          areaId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               dashBoardGetRewardIssuedByCampaignRequest({
                    roleId,
                    startDate,
                    endDate,
                    areaId,
                    applicationId,
               }),
          );
     };

     const getDevicedUsedSummaryData = (
          startDate = null,
          endDate = null,
          roleId = null,
          areaId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               getDevicedUsedSummaryRequest({
                    roleId,
                    startDate,
                    endDate,
                    areaId,
                    applicationId,
               }),
          );
     };

     const getUserLoginSummaryData = (
          startDate = null,
          endDate = null,
          roleId = null,
          areaId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               dashBoardGetParticipantLoginSummaryRequest({
                    roleId,
                    startDate,
                    endDate,
                    areaId,
                    applicationId,
               }),
          );
     };

     const getEmailTrackingSummaryData = (
          startDate = null,
          endDate = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               getEmailTrackingSummaryRequest({
                    startDate,
                    endDate,
                    applicationId,
               }),
          );
     };

     const getCampaignPageViewSummaryData = (
          startDate = null,
          endDate = null,
          roleId = null,
          areaId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               dashBoardGetCampaignPageViewSummaryRequest({
                    roleId,
                    startDate,
                    endDate,
                    areaId,
                    applicationId,
               }),
          );
     };

     const getCommsPageViewSummaryData = (
          startDate = null,
          endDate = null,
          roleId = null,
          areaId = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               dashBoardGetCommsPageViewSummaryRequest({
                    roleId,
                    startDate,
                    endDate,
                    areaId,
                    applicationId,
               }),
          );
     };

     const getEmailDispatchSummaryData = (
          startDate = null,
          endDate = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               getEmailDispatchSummaryRequest({
                    startDate,
                    endDate,
                    applicationId,
               }),
          );
     };

     const getMostActiveData = (
          startDate = null,
          endDate = null,
          applicationId = userType,
     ) => {
          actionDispatch(
               getMostActiveDetailChartDataRequest({
                    startDate,
                    endDate,
                    applicationId,
               }),
          );
     };

     const options = [
          { value: "0", label: "All" },
          { value: "1", label: "Equipe" },
          { value: "3", label: "TLC" },
     ];

     const [graphList, setGraphList] = useState([
          // {
          //      id: "1",
          //      type: "carousel",
          //      name: "",
          //      location: { x: 0, y: 0, w: 2, h: 1.5 },
          // },
          // {
          //      id: "2",
          //      type: "column",
          //      name: "Participant Activation Summary",
          //      location: { x: 0, y: 1, w: 1, h: 3.8 },
          // },
          // {
          //      id: "3",
          //      type: "column",
          //      name: "Reward Issued By Campaign",
          //      location: { x: 1, y: 1, w: 1, h: 3.8 },
          // },
          // {
          //      id: "4",
          //      type: "column",
          //      name: "Email Despatches",
          //      location: { x: 0, y: 7, w: 2, h: 3.8 },
          // },
          // {
          //      id: "5",
          //      type: "line",
          //      name: "User Login Summary",
          //      location: { x: 0, y: 2, w: 2, h: 3.8 },
          // },
          // {
          //      id: "6",
          //      type: "line",
          //      name: "Campaign Page View Summary",
          //      location: { x: 0, y: 4, w: 2, h: 3.8 },
          // },
          // {
          //      id: "7",
          //      type: "line",
          //      name: "Comms Page View Summary",
          //      location: { x: 0, y: 5, w: 2, h: 3.8 },
          // },
          // {
          //      id: "8",
          //      type: "line",
          //      name: "Device Used",
          //      location: { x: 0, y: 6, w: 2, h: 3.8 },
          // },
          // {
          //      id: "9",
          //      type: "line",
          //      name: "Email Tracking",
          //      location: { x: 0, y: 3, w: 2, h: 4.5 },
          // },
          // {
          //      id: "10",
          //      type: "donut",
          //      name: "Campaign Carousel",
          //      location: { x: 0, y: 7, w: 2, h: 3.75 },
          // },
     ]);

     // const { graphList } = useSelector((state) => {
     //      return { graphList: state.dashboard.graphList };
     // });
     const gridContainerRef = useRef(null);

     const [layouts, setLayouts] = useState([
          { x: 0, y: 0, w: 1, h: 2.5 },
          { x: 1, y: 0, w: 1, h: 2.5 },
          { x: 0, y: 2, w: 2, h: 2.5 },
          { x: 0, y: 3, w: 2, h: 1.5 },
          { x: 0, y: 4, w: 2, h: 2 },
     ]);

     const [newLayouts, setNewLayouts] = useState([]);

     const onLayoutChange = (layout) => {
          setLayouts(layout);
          setNewLayouts(layout.map(({ x, y, i }) => ({ x, y, i })));
     };

     // useEffect(() => {
     //      const updatedLayout = layouts.map((item1) => {
     //           const item2 = newLayouts.find((item) => item.i === item1.i);
     //           return { ...item1, ...item2 };
     //      });
     //      setLayouts(updatedLayout);
     // }, [newLayouts]);

     useEffect(() => {
          actionDispatch(getAreaListRequest({
               applicationId:1
          }));
          actionDispatch(getTLCAreaListRequest({
               applicationId:3
          }))
     }, []);

     const onDragStart = (layout) => {
          const handleScroll = (e) => {
               const gridContainer = gridContainerRef.current;
               if (!gridContainer) return;

               const buffer = 50;
               const scrollSpeed = 150;

               const { clientX, clientY } = e;

               if (clientY < buffer) {
                    document.body.scrollBy(0, -scrollSpeed);
               } else if (clientY > window.innerHeight - buffer) {
                    document.body.scrollBy(0, scrollSpeed);
               }
          };

          window.addEventListener("mousemove", handleScroll);

          const cleanup = () => {
               window.removeEventListener("mousemove", handleScroll);
          };

          window.addEventListener("mouseup", cleanup, { once: true });
     };

     return (
          <div className="dashboard-wrapper">
               <div ref={gridContainerRef} className="grid-container">
                    <ChatWindow />
               </div>
          </div>
     );
}

export default DashboardWrapper;
